import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form-v7';
import { RadioGroup, RadioGroupItem, Separator } from '@parrot-software/design-system';

import { WizardFormField } from '@onboarding/presentation/components/shared';
import { AddressSearchInput } from '@onboarding/presentation/components/AddressSearchInput';
import { useAddressSearch } from '@onboarding/presentation/hooks';
import type { WizardFormValues } from '@onboarding/presentation/types';

/** Props for the brand fiscal data choice section in the receipt step. */
type BrandFiscalSectionProps = {
  brandName: string;
  index: number;
  isLast: boolean;
  registeredLegalName: string;
  registeredRfc: string;
};

/**
 * Renders the fiscal data choice (registered vs custom) for a single brand.
 * Each brand can either reuse the store's fiscal data or provide new ones.
 */
export const BrandFiscalSection = ({
  brandName,
  index,
  isLast,
  registeredLegalName,
  registeredRfc,
}: BrandFiscalSectionProps) => {
  const { t } = useTranslation('onboarding');
  const { control } = useFormContext<WizardFormValues>();
  const addressSearch = useAddressSearch();

  const useRegistered = useWatch({ control, name: `receipt.brands.${index}.useRegisteredData` });
  const isCustom = useRegistered === 'false';

  return (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <div className="shrink-0 md:w-[160px] md:pt-5">
          <span className="block wrap-break-word text-sm font-semibold leading-5 text-foreground">
            {t('steps.receipt.brands.label', { name: brandName })}
          </span>
        </div>

        <Controller
          control={control}
          name={`receipt.brands.${index}.useRegisteredData`}
          render={({ field }) => (
            <RadioGroup
              className="flex flex-1 flex-col gap-4 md:flex-row"
              onValueChange={field.onChange}
              value={field.value}
            >
              {/* Option: Use registered data */}
              <label className="flex flex-1 cursor-pointer flex-col gap-5 rounded-xl border p-5 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{t('steps.receipt.brands.useRegistered')}</span>
                  <RadioGroupItem value="true" />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm leading-5 text-muted-foreground">
                  <span>{registeredLegalName}</span>
                  <span>{registeredRfc}</span>
                </div>
              </label>

              {/* Option: Use custom data */}
              <label className="flex flex-1 cursor-pointer flex-col gap-5 rounded-xl border p-5 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{t('steps.receipt.brands.useCustom')}</span>
                  <RadioGroupItem value="false" />
                </div>
                <p className="text-sm leading-5 text-muted-foreground">
                  {isCustom ? t('steps.receipt.brands.customSelected') : t('steps.receipt.brands.customPlaceholder')}
                </p>
              </label>
            </RadioGroup>
          )}
        />
      </div>

      {isCustom && (
        <div className="flex flex-col gap-4">
          <p className="text-base font-medium leading-6 text-foreground">
            {t('steps.receipt.brands.customFieldsTitle')}
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="order-2 md:order-0">
              <Controller
                control={control}
                name={`receipt.brands.${index}.legalName`}
                render={({ field, fieldState }) => (
                  <WizardFormField
                    error={fieldState.error?.message}
                    id={`brand-${index}-legal-name`}
                    label={t('steps.receipt.fields.legalName.label')}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder={t('steps.receipt.fields.legalName.placeholder')}
                    required
                    value={field.value}
                  />
                )}
              />
            </div>
            <div className="order-1 md:order-0">
              <Controller
                control={control}
                name={`receipt.brands.${index}.rfc`}
                render={({ field, fieldState }) => (
                  <WizardFormField
                    error={fieldState.error?.message}
                    id={`brand-${index}-rfc`}
                    label={t('steps.receipt.fields.rfc.label')}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder={t('steps.receipt.fields.rfc.placeholder')}
                    required
                    value={field.value}
                  />
                )}
              />
            </div>
            <div className="order-3 col-span-1 md:col-span-2 lg:col-span-3">
              <Controller
                control={control}
                name={`receipt.brands.${index}.fiscalAddress`}
                render={({ field, fieldState }) => (
                  <AddressSearchInput
                    error={fieldState.error?.message}
                    isLoading={addressSearch.isLoadingPredictions}
                    label={t('steps.receipt.fields.fiscalAddress.label')}
                    onChange={(value) => {
                      addressSearch.setQuery(value);
                      field.onChange(value);
                    }}
                    onSelect={(address) => {
                      field.onChange(address.formattedAddress);
                      addressSearch.setQuery(address.formattedAddress);
                    }}
                    placeholder={t('steps.receipt.fields.fiscalAddress.placeholder')}
                    predictions={addressSearch.predictions}
                    required
                    selectPlace={addressSearch.selectPlace}
                    value={field.value}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}

      {!isLast && <Separator />}
    </>
  );
};
